import { injectable, injectFromBase } from 'inversify'
import DomainBaseService from '@/services/domain/domainBaseService'
import { DomainSettings } from '@/decorators/domainDecorators'
import UiSettings from '@/data/models/domain/UiSettings'
import type UiSettingsRepository from '@/data/repositories/domain/uiSettingsRepository'
import type { ModelRaw } from '@/types'

@DomainSettings({
  model: UiSettings,
})
@injectable()
@injectFromBase()
export default class UiSettingsService extends DomainBaseService<UiSettingsRepository> {
  private readonly fallbackSettings: Omit<ModelRaw<UiSettings>, 'id'> = {
    // Hide onboarding page - most users have already dismissed it.
    ticketsPropsIsGettingStartedPageHidden: true,
    // null = consent not decided yet - triggers consent dialog. We must never assume `true` (legal).
    openAIConsent: null,
    // Hide tooltip - most users have already seen it.
    composeSwitchChannelsTooltipHidden: true,
    // Skip promo modal - avoids re-showing it to users who already closed it.
    emailCampaignIsPromotionalModalChecked: true,
    // Keep `false` - users must acknowledge deliverability rules before sending to protect domain reputation.
    emailCampaignWarmupGuidelinesChecked: false,
    // Keep `false` - users must see cleanup step before importing.
    contactsImportDataCleanupStepHidden: false,
    // Show tooltip - most users have not seen it yet.
    automationAiBuilderFeatureTooltipHidden: false,
  }

  public fetchSettings() {
    return this.getDomainRepository().fetchSettings()
  }

  public insertFallbackSettings() {
    return this.getDomainRepository().insertOrUpdateSettings(this.fallbackSettings)
  }

  public updateSettings(settings: Partial<ModelRaw<UiSettings>>) {
    return this.getDomainRepository().updateSettings(settings)
  }
}
